import { ErrorMessage, Form, Input } from '@/components';
import type { ComponentProps } from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp } from './types';
import { isTextFilled, transformFallbackValue } from './utils';

type Props<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  control: FormControlProp<T>;
  errors: FieldErrors;
  required?: boolean;
  disabled?: boolean;
  fallbackValue?: '' | null;
  rows?: number;
  showCount?: boolean;
  maxLength?: number;
  placeholder?: string;
  style?: ComponentProps<typeof Form.Item>['style'];
  hint?: string;
  labelSize?: FieldLabelSize;
};

export const TextAreaInputField = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  required,
  disabled,
  fallbackValue,
  rows,
  showCount,
  maxLength,
  placeholder,
  style,
  hint,
  labelSize = 'medium',
}: Props<T>) => {
  const transformedFallbackValue = transformFallbackValue(
    fallbackValue,
    required,
  );

  return (
    <Form.Item
      label={
        label && (
          <FieldLabel
            text={label}
            required={required}
            hint={hint}
            size={labelSize}
          />
        )
      }
      style={style}
    >
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, onBlur, ...field } }) => (
          <Input.TextArea
            {...field}
            onChange={(e) => {
              if (isTextFilled(e.target.value)) {
                onChange(e.target.value || transformedFallbackValue);
              }
            }}
            onBlur={(e) => {
              onChange(e.target.value.trim() || transformedFallbackValue);
              onBlur();
            }}
            autoSize={{
              minRows: rows,
            }}
            showCount={!!showCount || !!maxLength}
            maxLength={maxLength}
            placeholder={placeholder}
            disabled={disabled}
          />
        )}
      />
      <ErrorMessage errors={errors} name={name} />
    </Form.Item>
  );
};
